import { useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/redux/store";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { ProjectType } from "@commercial-publishing/shared-types/project";
import {
  api,
  useCreateReportExportMutation,
  useDeleteReportExportMutation,
  useGetReportExportsQuery,
} from "../../services/api";
import type { ReportExport } from "@commercial-publishing/shared-types/report";

const baseUrl = import.meta.env.VITE_BACKEND_BASE_URL;

const STATUS_LABEL: Record<ReportExport["status"], string> = {
  pending: "Pending",
  running: "Running",
  done: "Done",
  failed: "Failed",
};

const STATUS_COLOR: Record<ReportExport["status"], string> = {
  pending: "#f5b400",
  running: "#f5b400",
  done: "#2e9e5b",
  failed: "#d93025",
};

const StatusDot = ({ status }: { status: ReportExport["status"] }) => (
  <Box
    component="span"
    role="img"
    aria-label={STATUS_LABEL[status]}
    sx={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      marginRight: "8px",
      verticalAlign: "middle",
      backgroundColor: STATUS_COLOR[status],
    }}
  />
);

const isActive = (e: ReportExport) =>
  e.status === "pending" || e.status === "running";

const formatDay = (day: string) =>
  moment(day, "YYYY-MM-DD").format("DD.MM.YYYY");

export const downloadUrl = (projectId: string, exportId: string) =>
  `${baseUrl}/v1/reports/${projectId}/exports/${exportId}/download`;

interface Props {
  projectId: string;
  dateFrom?: string;
  dateTo?: string;
  projectType?: ProjectType;
}

const ReportExports = ({ projectId, dateFrom, dateTo, projectType }: Props) => {
  const isLoggedIn = useSelector((state: RootState) => state.isLoggedIn);

  // Read the cached list first so the polling interval for this render can be
  // derived from the latest known data (RTK Query lets it change per render).
  const { data: cached } =
    api.endpoints.getReportExports.useQueryState(projectId);
  const anyActive = cached?.some(isActive) ?? false;

  const { data: exports = [] } = useGetReportExportsQuery(projectId, {
    pollingInterval: anyActive ? 4000 : 0,
  });

  const [createExport, { isLoading: isCreating, isError: createFailed }] =
    useCreateReportExportMutation();
  const [deleteExport, { isLoading: isDeleting }] =
    useDeleteReportExportMutation();
  const [toDelete, setToDelete] = useState<ReportExport | null>(null);

  const confirmDelete = async () => {
    if (!toDelete) {
      return;
    }
    await deleteExport({ id: projectId, exportId: toDelete._id });
    setToDelete(null);
  };

  const showButton = isLoggedIn && projectType === ProjectType.Abnumber;

  if (!showButton && exports.length === 0) {
    return null;
  }

  return (
    <Box sx={{ marginTop: "16px" }}>
      {showButton && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Button
            variant="outlined"
            disabled={isCreating}
            onClick={() => createExport({ id: projectId, dateFrom, dateTo })}
            style={{
              textTransform: "none",
              borderRadius: "3px",
              boxShadow: "0 1px 1px 0 rgba(0, 0, 0, 0.09)",
              border: "solid 2px #223647",
              color: "#223647",
            }}
          >
            Export CSV
          </Button>
          {createFailed && (
            <Typography variant="body2" color="error">
              Export could not be started. Please try again.
            </Typography>
          )}
        </Box>
      )}
      {exports.length > 0 && (
        <Table size="small" sx={{ marginTop: "8px", maxWidth: "720px" }}>
          <TableHead>
            <TableRow>
              <TableCell>Date range</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {exports.map((e) => (
              <TableRow key={e._id}>
                <TableCell>
                  {formatDay(e.dateFrom)} – {formatDay(e.dateTo)}
                </TableCell>
                <TableCell>
                  {moment(e.createdAt).format("DD.MM.YYYY HH:mm")}
                </TableCell>
                <TableCell>
                  <StatusDot status={e.status} />
                  {STATUS_LABEL[e.status]}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: "12px" }}>
                    {e.status === "done" && (
                      <Link href={downloadUrl(projectId, e._id)} download>
                        Download
                      </Link>
                    )}
                    {isLoggedIn && (
                      <Link
                        component="button"
                        type="button"
                        color="error"
                        onClick={() => setToDelete(e)}
                      >
                        Delete
                      </Link>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={toDelete !== null} onClose={() => setToDelete(null)}>
        <DialogTitle>Delete export?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {toDelete &&
              `The export for ${formatDay(toDelete.dateFrom)} – ${formatDay(
                toDelete.dateTo,
              )} and its ZIP file will be removed. This cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="error" onClick={confirmDelete} disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportExports;
